/** @typedef {import('../index.js').InvalidDocData} InvalidDocData */
/** @typedef {import('./project.js').ProjectDocData} ProjectDocData */
import Project from './project.js';
import CustomerProject, { customerProjectSchema } from './customerProject.js';
import InternalProject, { internalProjectSchema } from './internalProject.js';

/**
 * Converts a Firestore document snapshot into a {@link Project} subclass instance.
 * @param {any} doc Raw Firestore document snapshot.
 * @returns A {@link Project} subclass instance.
 */
function projectFromFirestore(doc) {
  const docData = { id: doc.id, ...doc.data() };
  try {
    if (docData.isInternal === true) {
      return InternalProject.fromFirestore(docData);
    } if (docData.isInternal === false) {
      return CustomerProject.fromFirestore(docData);
    }
    throw new Error(
      `Cannot determine project type from Firestore data. Received: ${JSON.stringify(docData)}`,
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error transforming project data:', docData.id, error);
    /** @type {InvalidDocData} */
    const InvalidDocData = { validation: 'failed', ...docData };
    return InvalidDocData;
  }
}

/**
 * Converts a {@link Project} instance into a Firestore-compatible oject.
 * @returns A {@link ProjectDocData} object (without `id`).
 * @throws {Error} Cannot determine project type from {@link Project} instance.
 */
function projectToFirestore(project, serverTimestamp) {
  if (!serverTimestamp) {
    throw new Error('Missing serverTimestamp function');
  }
  switch (project.isInternal) {
    case true:
      return new InternalProject(project).toFirestore({ serverTimestamp });
    case false:
      return new CustomerProject(project).toFirestore({ serverTimestamp });
    default:
      throw new Error(
        `Cannot determine project type from project instance. Received: ${JSON.stringify(project)}`,
      );
  }
}

/**
 * Returns the appropriate Yup schema for a given project type.
 * @param {boolean} isInternal - Whether the project is internal.
 * @returns A Yup schema.
 * @throws {Error} Cannot determine project schema type from `isInternal` flag.
 */
function getProjectSchema(isInternal) {
  switch (isInternal) {
    case true:
      return internalProjectSchema;
    case false:
      return customerProjectSchema;
    default:
      throw new Error(
        `Cannot determine project schema type from isInternal flag. Received: ${isInternal}`,
      );
  }
}

export {
  projectFromFirestore,
  projectToFirestore,
  getProjectSchema,
};
